import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, Minus, Pause, Play, Plus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { BeatRow } from '@/components/BeatRow';
import { LevelWave } from '@/components/LevelWave';
import { MicGate } from '@/components/MicGate';
import { Button, Card, Segmented, SectionTitle } from '@/components/ui';
import { BPM_RANGE } from '@/config/audio';
import { DURATION_OPTIONS } from '@/config/training';
import { t } from '@/i18n';
import { audioEngine } from '@/audio/engine/AudioEngine';
import { useAudioLifecycle, useAudioState, useEngineStore } from '@/hooks/useAudio';
import { useFinishSession } from '@/hooks/useFinishSession';
import { usePreferences } from '@/hooks/usePreferences';
import { RhythmEngine, type RhythmSettings } from '@/training/rhythm/RhythmEngine';
import './RhythmPage.css';

/**
 * Rhythm: one downstroke per beat.
 *
 * Setup and exercise live on the same route — there are only two settings, and
 * a separate setup screen for two sliders would be ceremony.
 */
export function RhythmPage() {
  const navigate = useNavigate();
  const audio = useAudioState();
  const { prefs, update } = usePreferences();

  const [settings, setSettings] = useState<RhythmSettings>({
    bpm: prefs.lastBpm,
    durationMin: 3,
    clickEnabled: true,
  });
  const [running, setRunning] = useState(false);
  const engineRef = useRef<RhythmEngine | null>(null);
  const [engine, setEngine] = useState<RhythmEngine | null>(null);

  const handleFinished = useFinishSession();

  useEffect(() => {
    if (!running || audio.status !== 'ready' || engineRef.current) return;
    const created = new RhythmEngine(audioEngine, settings, handleFinished);
    engineRef.current = created;
    setEngine(created);
    created.start();
    return () => {
      created.dispose();
      engineRef.current = null;
    };
  }, [running, audio.status, settings, handleFinished]);

  useAudioLifecycle(
    useCallback(() => {
      engineRef.current?.pause();
    }, []),
  );

  return (
    <AppShell
      title={t('rhythm.title')}
      showBack
      // Leaving mid-exercise finishes it, so the practice already done is saved.
      onBack={() => (engineRef.current ? engineRef.current.finish() : navigate(-1))}
    >
      <MicGate>
        {running && engine ? (
          <RhythmSession engine={engine} />
        ) : (
          <>
            <Card className="rhythm__brief">
              <span className="rhythm__brief-icon">
                <ArrowDown size={26} aria-hidden="true" strokeWidth={2.5} />
              </span>
              <p className="rhythm__brief-text">{t('rhythm.instruction')}</p>
            </Card>

            <section className="page-section rhythm__setup">
              <div className="setup__label-row">
                <SectionTitle>{t('setup.tempo')}</SectionTitle>
                <span className="setup__value tnum">{t('setup.bpm', { value: settings.bpm })}</span>
              </div>
              <div className="slider-row">
                <button
                  type="button"
                  className="btn btn--icon"
                  onClick={() =>
                    setSettings((s) => ({ ...s, bpm: Math.max(BPM_RANGE.min, s.bpm - 5) }))
                  }
                  aria-label={t('setup.tempoDown')}
                >
                  <Minus size={20} aria-hidden="true" />
                </button>
                <input
                  className="slider"
                  type="range"
                  min={BPM_RANGE.min}
                  max={BPM_RANGE.max}
                  value={settings.bpm}
                  onChange={(event) => setSettings((s) => ({ ...s, bpm: Number(event.target.value) }))}
                  aria-label={t('setup.tempo')}
                  aria-valuetext={t('setup.bpm', { value: settings.bpm })}
                  style={
                    {
                      '--fill': `${((settings.bpm - BPM_RANGE.min) / (BPM_RANGE.max - BPM_RANGE.min)) * 100}%`,
                    } as React.CSSProperties
                  }
                />
                <button
                  type="button"
                  className="btn btn--icon"
                  onClick={() =>
                    setSettings((s) => ({ ...s, bpm: Math.min(BPM_RANGE.max, s.bpm + 5) }))
                  }
                  aria-label={t('setup.tempoUp')}
                >
                  <Plus size={20} aria-hidden="true" />
                </button>
              </div>
            </section>

            <section className="page-section">
              <SectionTitle>{t('setup.duration')}</SectionTitle>
              <Segmented
                label={t('setup.duration')}
                options={DURATION_OPTIONS}
                value={settings.durationMin}
                onChange={(value) => setSettings((s) => ({ ...s, durationMin: value }))}
                format={(value) => t('setup.durationOption', { count: value })}
              />
            </section>

            <Button
              large
              block
              onClick={() => {
                update({ lastBpm: settings.bpm });
                setRunning(true);
              }}
            >
              <Play size={20} fill="currentColor" aria-hidden="true" />
              {t('setup.start')}
            </Button>

            <p className="rhythm__note">{t('rhythm.latencyNote')}</p>
          </>
        )}
      </MicGate>
    </AppShell>
  );
}

function RhythmSession({ engine }: { engine: RhythmEngine }) {
  const snapshot = useEngineStore(engine.store);
  const paused = snapshot.phase === 'paused';

  return (
    <>
      <Card section className="rhythm__stage">
        {snapshot.phase === 'countIn' ? (
          <div className="active__countin" aria-live="polite">
            <span className="active__countin-number tnum">{snapshot.countInBeat}</span>
            <span className="active__countin-label">{t('active.countIn')}</span>
          </div>
        ) : (
          <>
            <span className="rhythm__stroke" aria-hidden="true">
              <ArrowDown size={72} strokeWidth={2.5} />
            </span>
            <p className="rhythm__prompt">{t('rhythm.instruction')}</p>
            <BeatRow count={4} active={snapshot.beatInBar} bpm={snapshot.bpm} />
            <p className="active__bpm tnum">{t('setup.bpm', { value: snapshot.bpm })}</p>
          </>
        )}
      </Card>

      <div className="rhythm__result" aria-live="polite">
        {snapshot.lastVerdict && (
          <span className={`rhythm__verdict rhythm__verdict--${snapshot.lastVerdict}`}>
            {t(`rhythm.${snapshot.lastVerdict}`)}
          </span>
        )}
        <span className="rhythm__score tnum">
          {t('rhythm.score')} {snapshot.score}%
        </span>
      </div>

      <div className="listening listening--on">
        <LevelWave bars={30} height={26} color="var(--violet)" paused={paused} />
      </div>

      <div className="active__actions">
        <Button variant="secondary" onClick={() => (paused ? engine.resume() : engine.pause())}>
          {paused ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}
          {paused ? t('active.resume') : t('active.pause')}
        </Button>
        <Button onClick={() => engine.finish()}>{t('active.finish')}</Button>
      </div>

      <p className="rhythm__note">{t('rhythm.latencyNote')}</p>
    </>
  );
}
